import {
  areAnyChipTextsSelected,
  getParameterValue,
  isAChipInSubtitleSelected,
  isChipTextSelected,
} from '~/src/dash/endpoint-builder-configs/helpers';
import { getResellerTitle } from '~/src/dash/resellers/utils/importResellerConfig';
import {
  EndpointBuilderService,
  IEndpointBuilderChip,
  IEndpointBuilderRoot,
  SelectType,
} from '~/src/dash/utils/endpointBuilderService';

export const getEndpointBuilderService = (getChannelID: () => string): EndpointBuilderService => {
  // create new endpoint-builder service for site-settings-list
  const service: EndpointBuilderService = new EndpointBuilderService();

  // set structure for endpoint service
  const endpointBuilderStructure: IEndpointBuilderRoot = {
    defaults: [
      { parameterName: 'site_id', makeParameterValue: () => getChannelID() },
      {
        parameterName: 'app_bundle',
        setByParams: true,
        makeParameterValue: () =>
          getParameterValue(
            service,
            'ROKU_ADS_APP_ID',
            '[REPLACE_MACRO]',
            '[REPLACE_MACRO]',
            '{{APP_BUNDLE}}',
            '[APP_BUNDLE]',
          ),
      },
      {
        parameterName: 'did',
        isActive: () =>
          !!(service.getChip('Required Parameters', 'Type', 'App') || { selected: false }).selected,
        makeParameterValue: () =>
          getParameterValue(
            service,
            'ROKU_ADS_TRACKING_ID',
            '%%25ADVERTISING_IDENTIFIER_PLAIN%%',
            '#{request.deviceId}',
            '{{DEVICE_ID}}',
            '[DID]',
          ),
      },
      {
        parameterName: 'site_page',
        isActive: () =>
          !!(service.getChip('Required Parameters', 'Type', 'Website') || { selected: false })
            .selected,
        makeParameterValue: () =>
          getParameterValue(
            service,
            '[REPLACE_MACRO]',
            '%%PATTERN:url%%',
            '#e{request.pageUrl}',
            '{{ENCODED_URL}}',
            '[SITE_PAGE]',
          ),
      },
    ],
    titles: [
      {
        title: 'Required Parameters',
        subtitles: [
          {
            subtitle: 'Type',
            description: `Define if you will be calling the ${getResellerTitle()} endpoint from an app (Mobile or CTV) or a website.`,
            selectType: SelectType.SINGLE,
            chips: [
              {
                text: 'App',
                // Selected if app_bundle was passed in from the intial params or the subtitles were selected
                isSelected: () =>
                  !!(service.getDefault('app_bundle') || { active: false }).active ||
                  isAChipInSubtitleSelected(service, 'Additional Parameters', 'App'),
                clickHandle: (selected: boolean) => {
                  // when App is clicked active, set app_bundle manually
                  if (selected) {
                    (
                      service.getSubtitle('Additional Parameters', 'App') || { chips: [] }
                    ).chips.forEach((chip: IEndpointBuilderChip) => {
                      chip.selected = true;
                    });
                    (
                      service.getChip('Additional Parameters', 'Other', 'Device ID Type') || {
                        selected: false,
                      }
                    ).selected = true;
                    // set app_bundle manually
                    (service.getDefault('app_bundle') || { active: false }).active = true;
                  } else {
                    (service.getDefault('app_bundle') || { active: false }).active = false;
                  }
                },
              },
              {
                text: 'Website',
                isSelected: () =>
                  isAChipInSubtitleSelected(service, 'Additional Parameters', 'Site'),
                clickHandle: (selected: boolean) => {
                  // when Site is clicked active, select relative params
                  if (selected) {
                    (
                      service.getSubtitle('Additional Parameters', 'Site') || { chips: [] }
                    ).chips.forEach((chip: IEndpointBuilderChip) => {
                      chip.selected = true;
                    });
                    // Hack to get app_bundle to de-select caused by setByParams
                    (service.getDefault('app_bundle') || { active: false }).active = false;
                  }
                },
              },
              {
                text: 'DOOH',
                isSelected: () =>
                  isAChipInSubtitleSelected(service, 'Additional Parameters', 'DOOH'),
                clickHandle: (selected: boolean) => {
                  if (selected) {
                    (
                      service.getSubtitle('Additional Parameters', 'DOOH') || { chips: [] }
                    ).chips.forEach((chip: IEndpointBuilderChip) => {
                      chip.selected = true;
                    });
                    // Hack to get app_bundle to de-select caused by setByParams
                    (service.getDefault('app_bundle') || { active: false }).active = false;

                    // when Dooh is clicked active, set app default values app_bundle manually
                    if (selected) {
                      (
                        service.getSubtitle('Additional Parameters', 'App') || { chips: [] }
                      ).chips.forEach((chip: IEndpointBuilderChip) => {
                        chip.selected = true;
                      });
                      (
                        service.getChip('Additional Parameters', 'Other', 'Device ID Type') || {
                          selected: false,
                        }
                      ).selected = true;
                      // set app_bundle manually
                      (service.getDefault('app_bundle') || { active: false }).active = true;
                    } else {
                      (service.getDefault('app_bundle') || { active: false }).active = false;
                    }
                  }
                },
              },
            ],
          },
          {
            subtitle: 'Integration Type',
            description: `Please ask your ${getResellerTitle()} account manager or refer to our documentation if you aren’t sure which integration type is right for you.`,
            selectType: SelectType.SINGLE,
            chips: [
              { text: 'JSON', parameterName: 'format', parameterValue: 'json' },
              { text: 'VAST', parameterName: 'format', parameterValue: 'vast' },
              {
                text: 'VMAP',
                parameterName: 'format',
                parameterValue: 'vmap',
                clickHandle: (selected: boolean) =>
                  ((
                    service.getChip('Required Parameters', 'VMAP Ad Break', 'With Slot Count') || {
                      selected: false,
                    }
                  ).selected = selected),
              },
            ],
          },
          {
            subtitle: 'VMAP Ad Break',
            description: 'VMAP Ad Break can include / exclude slot count as a param',
            selectType: SelectType.SINGLE,
            isVisible: () =>
              isChipTextSelected(service, 'Required Parameters', 'Integration Type', 'VMAP'),
            chips: [
              {
                text: 'With Slot Count',
                parameterName: 'ad_break',
                parameterValue: '[AD_BREAK_ID],[TIME_OFFSET],[VMAP_POD_DURATION],[SLOT_COUNT]',
              },
              {
                text: 'No Slot Count',
                parameterName: 'ad_break',
                parameterValue: '[AD_BREAK_ID],[TIME_OFFSET],[VMAP_POD_DURATION]',
              },
            ],
          },
        ],
      },
      {
        title: 'Recommended Parameters',
        subtitles: [
          {
            subtitle: 'Ad Break Type',
            description:
              'Ad Pods can be defined either as a duration in seconds or by the number of slots available in the pod.',
            selectType: SelectType.SINGLE,
            isVisible: () =>
              !isChipTextSelected(service, 'Required Parameters', 'Integration Type', 'VMAP'),
            chips: [
              {
                text: 'Pod Duration',
                parameterName: 'pod_duration',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '#{slot.maxTotalDuration}',
                    '{{POD_MAX_DUR}}',
                    '[POD_DURATION]',
                  ),
                softParameterValue: true,
                // disabled if VMAP integration type is enabled
                isActive: () =>
                  !isChipTextSelected(service, 'Required Parameters', 'Integration Type', 'VMAP'),
              },
              {
                text: 'Slot Count',
                parameterName: 'slot_count',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '1',
                    '#{slot.maxAds}',
                    '{{POD_AD_SLOTS}}',
                    '[SLOT_COUNT]',
                  ),
                softParameterValue: true,
                // disabled if VMAP integration type is enabled
                isActive: () =>
                  !isChipTextSelected(service, 'Required Parameters', 'Integration Type', 'VMAP'),
              },
            ],
          },
          {
            subtitle: 'Minimum Ad Duration (optional)',
            description: 'Minimum ad duration in seconds',
            selectType: SelectType.SINGLE,
            isVisible: () =>
              areAnyChipTextsSelected(service, 'Recommended Parameters', 'Ad Break Type', [
                'Pod Duration',
                'Slot Count',
              ]) &&
              !areAnyChipTextsSelected(service, 'Trafficked In', 'root', [
                'GAM',
                'Roku',
                'SpringServe',
                'Freewheel',
              ]),
            chips: [
              { text: '6', parameterName: 'min_ad_duration', parameterValue: '6' },
              {
                text: '15',
                parameterName: 'min_ad_duration',
                isActive: () =>
                  !areAnyChipTextsSelected(
                    service,
                    'Recommended Parameters',
                    'Maximum Ad Duration (optional)',
                    ['6'],
                  ),
                parameterValue: '15',
              },
              {
                text: '30',
                parameterName: 'min_ad_duration',
                isActive: () =>
                  !areAnyChipTextsSelected(
                    service,
                    'Recommended Parameters',
                    'Maximum Ad Duration (optional)',
                    ['6', '15'],
                  ),
                parameterValue: '30',
              },
              {
                text: '45',
                parameterName: 'min_ad_duration',
                isActive: () =>
                  !areAnyChipTextsSelected(
                    service,
                    'Recommended Parameters',
                    'Maximum Ad Duration (optional)',
                    ['6', '15', '30'],
                  ),
                parameterValue: '45',
              },
              {
                text: '60',
                parameterName: 'min_ad_duration',
                isActive: () =>
                  !areAnyChipTextsSelected(
                    service,
                    'Recommended Parameters',
                    'Maximum Ad Duration (optional)',
                    ['6', '15', '30', '45'],
                  ),
                parameterValue: '60',
              },
              {
                text: '90',
                parameterName: 'min_ad_duration',
                isActive: () =>
                  !areAnyChipTextsSelected(
                    service,
                    'Recommended Parameters',
                    'Maximum Ad Duration (optional)',
                    ['6', '15', '30', '45', '60'],
                  ),
                parameterValue: '90',
              },
              {
                text: '120',
                parameterName: 'min_ad_duration',
                isActive: () =>
                  !areAnyChipTextsSelected(
                    service,
                    'Recommended Parameters',
                    'Maximum Ad Duration (optional)',
                    ['6', '15', '30', '45', '60', '90'],
                  ),
                parameterValue: '120',
              },
            ],
          },
          {
            subtitle: 'Maximum Ad Duration (optional)',
            description: 'Maximum ad duration in seconds',
            selectType: SelectType.SINGLE,
            isVisible: () =>
              areAnyChipTextsSelected(service, 'Recommended Parameters', 'Ad Break Type', [
                'Pod Duration',
                'Slot Count',
              ]) &&
              !areAnyChipTextsSelected(service, 'Trafficked In', 'root', [
                'GAM',
                'Roku',
                'SpringServe',
                'Freewheel',
              ]),
            chips: [
              {
                text: '6',
                parameterName: 'max_ad_duration',
                isActive: () =>
                  !areAnyChipTextsSelected(
                    service,
                    'Recommended Parameters',
                    'Minimum Ad Duration (optional)',
                    ['120', '90', '60', '45', '30', '15'],
                  ),
                parameterValue: '6',
              },
              {
                text: '15',
                parameterName: 'max_ad_duration',
                isActive: () =>
                  !areAnyChipTextsSelected(
                    service,
                    'Recommended Parameters',
                    'Minimum Ad Duration (optional)',
                    ['120', '90', '60', '45', '30'],
                  ),
                parameterValue: '15',
              },
              {
                text: '30',
                parameterName: 'max_ad_duration',
                isActive: () =>
                  !areAnyChipTextsSelected(
                    service,
                    'Recommended Parameters',
                    'Minimum Ad Duration (optional)',
                    ['120', '90', '60', '45'],
                  ),
                parameterValue: '30',
              },
              {
                text: '45',
                parameterName: 'max_ad_duration',
                isActive: () =>
                  !areAnyChipTextsSelected(
                    service,
                    'Recommended Parameters',
                    'Minimum Ad Duration (optional)',
                    ['120', '90', '60'],
                  ),
                parameterValue: '45',
              },
              {
                text: '60',
                parameterName: 'max_ad_duration',
                isActive: () =>
                  !areAnyChipTextsSelected(
                    service,
                    'Recommended Parameters',
                    'Minimum Ad Duration (optional)',
                    ['120', '90'],
                  ),
                parameterValue: '60',
              },
              {
                text: '90',
                parameterName: 'max_ad_duration',
                isActive: () =>
                  !areAnyChipTextsSelected(
                    service,
                    'Recommended Parameters',
                    'Minimum Ad Duration (optional)',
                    ['120'],
                  ),
                parameterValue: '90',
              },
              { text: '120', parameterName: 'max_ad_duration', parameterValue: '120' },
            ],
          },
          {
            subtitle: 'Ad Duration (optional)',
            description: 'Set min and max ad duration (depending on trafficking partner)',
            selectType: SelectType.MULTIPLE,
            isVisible: () =>
              areAnyChipTextsSelected(service, 'Recommended Parameters', 'Ad Break Type', [
                'Pod Duration',
                'Slot Count',
              ]) &&
              areAnyChipTextsSelected(service, 'Trafficked In', 'root', [
                'SpringServe',
                'Freewheel',
              ]),
            chips: [
              {
                text: 'Min Ad Duration',
                parameterName: 'min_ad_duration',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '{{MINIMUM_DURATION}}',
                    '[MIN_AD_DURATION]',
                  ),
                softParameterValue: true,
                // visible for SpringServe
                isVisible: () =>
                  isChipTextSelected(service, 'Trafficked In', 'root', 'SpringServe'),
              },
              {
                text: 'Max Ad Duration',
                parameterName: 'max_ad_duration',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '#{slot.maxTotalDuration}',
                    '{{MAXIMUM_DURATION}}',
                    '[MAX_AD_DURATION]',
                  ),
                softParameterValue: true,
                // visible for Freewheel or SpringServe
                isVisible: () =>
                  areAnyChipTextsSelected(service, 'Trafficked In', 'root', [
                    'SpringServe',
                    'Freewheel',
                  ]),
              },
            ],
          },
          {
            subtitle: 'Other',
            selectType: SelectType.MULTIPLE,
            chips: [
              {
                text: 'Cache Buster',
                parameterName: 'cb',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    'ROKU_ADS_CACHE_BUSTER',
                    '%%25CACHEBUSTER%%',
                    '#{ad.ref.random}',
                    '{{CACHEBUSTER}}',
                    '[CACHEBUSTER]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'IP',
                parameterName: 'ip',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    'ROKU_ADS_EXTERNAL_IP',
                    '[REPLACE_MACRO]',
                    '#e{request.clientAddress}',
                    '{{IP}}',
                    '[IP]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'User Agent',
                parameterName: 'ua',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    'ROKU_ADS_USER_AGENT',
                    '[REPLACE_MACRO]',
                    '#e{request.userAgent}',
                    '{{USER_AGENT}}',
                    '[UA]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Player Height',
                parameterName: 'player_height',
                parameterDeprecatedNames: ['h'],
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    'ROKU_ADS_DISPLAY_HEIGHT',
                    '%%HEIGHT%%',
                    '#{slot.height}',
                    '{{HEIGHT}}',
                    '[HEIGHT]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Player Width',
                parameterName: 'player_width',
                parameterDeprecatedNames: ['w'],
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    'ROKU_ADS_DISPLAY_WIDTH',
                    '%%WIDTH%%',
                    '#{slot.width}',
                    '{{WIDTH}}',
                    '[WIDTH]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Limited Ad Tracking',
                parameterName: 'is_lat',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    'ROKU_ADS_LIMIT_TRACKING',
                    '%%25ADVERTISING_IDENTIFIER_IS_LAT%%',
                    '#{request.keyValue("_fw_is_lat")}',
                    '{{DNT}}',
                    '[LIMITED_AD_TRACKING]',
                  ),
                softParameterValue: true,
              },
            ],
          },
        ],
      },
      {
        title: 'Additional Parameters',
        description:
          'The parameters are optional parameters that will help increase bidder participation rate within your auctions.',
        subtitles: [
          {
            subtitle: 'App',
            selectType: SelectType.MULTIPLE,
            isVisible: () =>
              service.isOneOfChipSelected('Required Parameters', 'Type', ['App', 'DOOH']),
            chips: [
              {
                text: 'App Category',
                parameterName: 'app_category',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[APP_CATEGORY]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'App Domain',
                parameterName: 'app_domain',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '%%SITE%%',
                    '#{request.referrerDomain}',
                    '{{DOMAIN}}',
                    '[APP_DOMAIN]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'App Name',
                parameterName: 'app_name',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '{{APP_NAME}}',
                    '[APP_NAME]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'App Store URL',
                parameterName: 'app_store_url',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '{{APP_STORE_URL}}',
                    '[APP_STORE_URL]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'App Version',
                parameterName: 'app_version',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    'ROKU_ADS_APP_VERSION',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[APP_VERSION]',
                  ),
                softParameterValue: true,
              },
            ],
          },
          {
            subtitle: 'Site',
            selectType: SelectType.MULTIPLE,
            isVisible: () =>
              !!(service.getChip('Required Parameters', 'Type', 'Website') || { selected: false })
                .selected,
            chips: [
              {
                text: 'Site Name',
                parameterName: 'site_name',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '%%SITE%%',
                    '#e{site.title}',
                    '{{DOMAIN}}',
                    '[CHANNEL_NAME]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Site Page',
                parameterName: 'site_page',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '%%PATTERN:url%%',
                    '#e{request.pageUrl}',
                    '{{ENCODED_URL}}',
                    '[PAGE_URL]',
                  ),
                softParameterValue: true,
              },
            ],
          },
          {
            subtitle: 'DOOH',
            selectType: SelectType.MULTIPLE,
            isVisible: () =>
              !!(service.getChip('Required Parameters', 'Type', 'DOOH') || { selected: false })
                .selected,
            chips: [
              {
                text: 'Venue ID',
                parameterName: 'venue_id',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[VENUEID]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Venue Type ID',
                parameterName: 'venue_type_id',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[VENUETYPEID]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Position Type',
                parameterName: 'position_type',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[POSITIONTYPE]',
                  ),
                softParameterValue: true,
              },
            ],
          },
          {
            subtitle: 'Content',
            selectType: SelectType.MULTIPLE,
            chips: [
              {
                text: 'Content Category',
                parameterName: 'content_cat',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '%%IAB_CATEGORIES%%',
                    '[REPLACE_MACRO]',
                    '{{IAB_CATEGORY}}',
                    '[CONTENT_CAT]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Channel',
                parameterName: 'content_channel',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CONTENT_CHANNEL]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Context',
                parameterName: 'content_context',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CONTENT_CONTEXT]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Distribution ID',
                parameterName: 'content_dist_id',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CONTENT_DIST_ID]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Distribution Name',
                parameterName: 'content_dist_name',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CONTENT_DIST_NAME]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Episode',
                parameterName: 'content_episode',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '{{CONTENT_EPISODE}}',
                    '[CONTENT_EPISODE]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Genre',
                parameterName: 'content_genre',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    'ROKU_ADS_CONTENT_GENRE',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '{{CONTENT_GENRE}}',
                    '[CONTENT_GENRE]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content ID',
                parameterName: 'content_id',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    'ROKU_ADS_CONTENT_ID',
                    '%%VIDEO_ID%%',
                    '#{asset.id}',
                    '{{CONTENT_ID}}',
                    '[CONTENT_ID]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Keywords',
                parameterName: 'content_keywords',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '{{KEYWORDS}}',
                    '[CONTENT_KEYWORDS]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Language',
                parameterName: 'content_language',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '{{LANGUAGE}}',
                    '[CONTENT_LANGUAGE]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Length',
                parameterName: 'content_length',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    'ROKU_ADS_CONTENT_LENGTH',
                    '%%VIDEO_DURATION%%',
                    '#{request.mediaDuration}',
                    '[REPLACE_MACRO]',
                    '[CONTENT_LENGTH]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Network',
                parameterName: 'content_network',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CONTENT_NETWORK]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Prod. Quality',
                parameterName: 'content_prodqual',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '{{PRODUCTION_QUALITY}}',
                    '[CONTENT_PROD]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Producer ID',
                parameterName: 'content_producer_id',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CONTENT_PRODUCER_ID]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Producer',
                parameterName: 'content_producer',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '{{CONTENT_PRODUCER_NAME}}',
                    '[CONTENT_PRODUCER_NAME]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Rating',
                parameterName: 'content_rating',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '{{RATING}}',
                    '[CONTENT_RATING]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Season',
                parameterName: 'content_season',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '{{CONTENT_SEASON}}',
                    '[CONTENT_SEASON]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Series',
                parameterName: 'content_series',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '#e{series.title}',
                    '{{CONTENT_SERIES}}',
                    '[CONTENT_SERIES]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content Title',
                parameterName: 'content_title',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '%%VIDEO_TITLE%%',
                    '#e{asset.title}',
                    '{{ENCODED_VIDEO_TITLE}}',
                    '[CONTENT_TITLE]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Content URL',
                parameterName: 'content_url',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CONTENT_URL]',
                  ),
                softParameterValue: true,
              },
            ],
          },
          {
            subtitle: 'Other',
            selectType: SelectType.MULTIPLE,
            chips: [
              {
                text: 'Buyer UID',
                parameterName: 'buyeruid',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[BUYERUID]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'CCPA',
                parameterName: 'us_privacy',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '#{request.keyValue("_fw_us_privacy")}',
                    '{{US_PRIVACY}}',
                    '[US_PRIVACY]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Consent',
                parameterName: 'consent',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '#{request.keyValue("_fw_gdpr_consent")}',
                    '{{CONSENT}}',
                    '[CONSENT]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Coppa',
                parameterName: 'coppa',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    'ROKU_ADS_KIDS_CONTENT',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '{{COPPA}}',
                    '[COPPA]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Custom 5',
                parameterName: 'custom_5',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CUSTOM_5]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Custom 6',
                parameterName: 'custom_6',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CUSTOM_6]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Custom 7',
                parameterName: 'custom_7',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CUSTOM_7]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Custom 8',
                parameterName: 'custom_8',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CUSTOM_8]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Custom 9',
                parameterName: 'custom_9',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CUSTOM_9]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Custom 10',
                parameterName: 'custom_10',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CUSTOM_10]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Custom 11',
                parameterName: 'custom_11',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CUSTOM_11]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Custom 12',
                parameterName: 'custom_12',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CUSTOM_12]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Custom 13',
                parameterName: 'custom_13',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CUSTOM_13]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Custom 14',
                parameterName: 'custom_14',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CUSTOM_14]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Custom 15',
                parameterName: 'custom_15',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CUSTOM_15]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Custom 16',
                parameterName: 'custom_16',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CUSTOM_16]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Custom 17',
                parameterName: 'custom_17',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CUSTOM_17]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Custom 18',
                parameterName: 'custom_18',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[CUSTOM_18]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Device Language',
                parameterName: 'device_language',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[DEVICE_LANGUAGE]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Device Latitude',
                parameterName: 'lat',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[LAT]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Device Longitude',
                parameterName: 'lon',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[LON]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Device Type',
                parameterName: 'device_type',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[DEVICE_TYPE]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'GDPR',
                parameterName: 'gdpr',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '#{request.keyValue("_fw_gdpr")}',
                    '{{GDPR}}',
                    '[GDPR]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Gender',
                parameterName: 'gender',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[GENDER]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Genre ID',
                parameterName: 'genre_id',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[GENRE_ID]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'IFA Type',
                parameterName: 'ifa_type',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    'RIDA',
                    '%%25ADVERTISING_IDENTIFIER_TYPE%%',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[IFA_TYPE]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Iris Context',
                parameterName: 'iris_context',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[IRIS_CONTEXT]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Iris ID',
                parameterName: 'iris_id',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[IRIS_ID]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Livestream',
                parameterName: 'livestream',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '{{CONTENT_LIVESTREAM}}',
                    '[LIVESTREAM]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Max Bitrate',
                parameterName: 'max_bitrate',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[MAXBITRATE]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Min Bitrate',
                parameterName: 'min_bitrate',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[MINBITRATE]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Position',
                parameterName: 'position',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[POSITION]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Preferred Language',
                parameterName: 'preferred_language',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[PREFERRED_LANGUAGE]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Rights Holder',
                parameterName: 'rights_holder',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[RIGHTS_HOLDER]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'SSAI Enabled',
                parameterName: 'ssai_enabled',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[SSAI_ENABLED]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'SSAI Vendor',
                parameterName: 'ssai_vendor',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[SSAI_VENDOR]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Session ID',
                parameterName: 'session_id',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[SESSION_ID]',
                  ),
                softParameterValue: true,
              },
              {
                text: 'Supply Chain',
                parameterName: 'schain',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '{{SCHAIN}}',
                    '[SCHAIN]',
                  ),
                softParameterValue: true,
              },
              {
                text: "User's Year Of Birth",
                parameterName: 'yob',
                makeParameterValue: () =>
                  getParameterValue(
                    service,
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[REPLACE_MACRO]',
                    '[YOB]',
                  ),
                softParameterValue: true,
              },
            ],
          },
        ],
      },
      {
        title: 'Trafficked In',
        subtitles: [
          {
            ignore: true, // dont'display this subtitle
            subtitle: 'root', // is still used to get chips event if it's not displayed
            description: '',
            selectType: SelectType.SINGLE,
            chips: [
              { text: 'GAM', parameterName: 'adserver', parameterValue: 'gam' },
              { text: 'Roku', parameterName: 'adserver', parameterValue: 'roku' },
              { text: 'SpringServe', parameterName: 'adserver', parameterValue: 'ss' },
              { text: 'Freewheel', parameterName: 'adserver', parameterValue: 'fw' },
            ],
          },
        ],
      },
    ],
  };

  service.setStructure(endpointBuilderStructure);
  return service;
};
